import React, { useState, useRef, useEffect, forwardRef } from 'react'
import { CheckboxContext } from './checkbox-context'
import { getState, isIndeterminate } from '../utils'
import {
  CheckboxContainer,
  CheckboxStandard,
  CheckboxEmphasis,
  CheckboxError
} from '../styled-components'
import { checkboxVariantOptions, checkboxStyleOptions } from '../constants'
import PropTypes from 'prop-types'
import { useComposedRefs } from '../../../../../utils/customHooks'

export const checkboxVariantComponentOptions = {
  [checkboxVariantOptions.STANDARD]: CheckboxStandard,
  [checkboxVariantOptions.EMPHASIS]: CheckboxEmphasis,
  [checkboxVariantOptions.ERROR]: CheckboxError
}

const CheckboxRoot = forwardRef((props, forwardedRef) => {
  const {
    name,
    checked: checkedProp,
    defaultChecked,
    required,
    disabled,
    onCheckedChange,
    variant,
    size,
    label,
    customOnClick,
    ...checkboxProps
  } = props
  const [button, setButton] = useState(null)
  const buttonRef = useRef(button)
  const composedRefs = useComposedRefs(forwardedRef, buttonRef)
  
  const hasConsumerStoppedPropagationRef = useRef(false)
  const isFormControl = button ? Boolean(button.closest('form')) : true
  const [checked = false, setChecked] = useState(defaultChecked)
  const initialCheckedStateRef = useRef(checked)

  const setCheckedReal = onCheckedChange ?? setChecked
  const checkedReal = checkedProp ?? checked

  const Component = checkboxVariantComponentOptions[variant] ?? CheckboxStandard

  useEffect(() => {
    const form = button?.form
    if (form) {
      const reset = () => setCheckedReal(initialCheckedStateRef.current)
      form.addEventListener('reset', reset)
      return () => form.removeEventListener('reset', reset)
    }
  }, [button, setCheckedReal])

  return (
    <CheckboxContainer size={size}>
      <CheckboxContext.Provider value={{ state: checkedReal, disabled, size }}>
        <Component
          size={size}
          type="button"
          role="checkbox"
          aria-checked={isIndeterminate(checkedReal) ? 'mixed' : checked}
          aria-required={required}
          data-state={getState(checkedReal)}
          data-disabled={disabled ? '' : undefined}
          disabled={disabled}
          value={checkedReal}
          ref={composedRefs}
          onKeyDown={(event) => {
            // According to WAI ARIA, Checkboxes don't activate on enter keypress
            if (event.key === 'Enter') event.preventDefault()
          }}
          onClick={(event) => {
            setCheckedReal((prevChecked) =>
              isIndeterminate(prevChecked) ? true : !prevChecked
            )
            if (isFormControl) {
              hasConsumerStoppedPropagationRef.current =
                event.isPropagationStopped()
              if (!hasConsumerStoppedPropagationRef.current) { event.stopPropagation() }
            }

            // Using a setTimeout with 0 ensures the customOnClick waits for the state to update
            setTimeout(() => {
              customOnClick && customOnClick(event)
            }, 0)
          }}
          {...checkboxProps}
        />
        {isFormControl && (
          <BubbleInput
            bubbles={!hasConsumerStoppedPropagationRef.current}
            name={name}
            value={checkedReal}
            checked={checkedReal}
            required={required}
            disabled={disabled}
            style={{ transform: 'translateX(-100%)' }}
          />
        )}
      </CheckboxContext.Provider>
      {label && <label>{label}</label>}
    </CheckboxContainer>
  )
})

const BubbleInput = (props) => {
  const { checked, bubbles = true, ...inputProps } = props

  const ref = useRef(null)
  const prevChecked = useRef(checked)

  useEffect(() => {
    const input = ref.current
    const descriptor = Object.getOwnPropertyDescriptor(input, 'checked')
    const setChecked = descriptor.set

    if (prevChecked.current !== checked && setChecked) {
      const event = new Event('click', { bubbles })
      input.indeterminate = isIndeterminate(checked)
      setChecked.call(input, isIndeterminate(checked) ? false : checked)
      input.dispatchEvent(event)
    }
  }, [prevChecked, checked, bubbles])

  return (
    <input
      type="checkbox"
      aria-hidden
      defaultChecked={isIndeterminate(checked) ? false : checked}
      {...inputProps}
      tabIndex={-1}
      ref={ref}
      style={{
        ...props.style,
        position: 'absolute',
        pointerEvents: 'none',
        opacity: 0,
        margin: 0
      }}
    />
  )
}

CheckboxRoot.propTypes = {
  label: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(checkboxStyleOptions)),
  variant: PropTypes.oneOf(Object.keys(checkboxVariantComponentOptions)),
  checked: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf(['indeterminate'])
  ]),
  defaultChecked: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf(['indeterminate'])
  ]),
  required: PropTypes.bool,
  onCheckedChange: PropTypes.func
}

CheckboxRoot.displayName = 'CheckboxRoot'
export default CheckboxRoot
